/**
 * DatasetEditor - Dataset editor/details panel component
 * 
 * Shows dataset details or edit form based on mode
 * 
 * CHANGE HISTORY:
 * - 251201_0155 danielbeer: Created dataset editor panel component
 */

import React, { useState, useEffect } from 'react';
import type { Dataset, DatasetConfig, DataSourceConfig } from '../types/DatasetTypes';

export interface DatasetEditorProps {
  dataset: Dataset | null;
  isEditing: boolean;
  isCreating: boolean;
  onSave: (dataset: DatasetConfig) => void;
  onCancel: () => void;
  onEdit: (dataset: Dataset) => void;
  onDelete: (dataset: Dataset) => void;
  onExport: (dataset: Dataset) => void;
}

export function DatasetEditor({
  dataset,
  isEditing,
  isCreating,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  onExport
}: DatasetEditorProps) {
  // Edit form state
  const [editForm, setEditForm] = useState<Partial<DatasetConfig>>({
    name: '',
    description: '',
    status: 'draft',
    sources: [],
    fields: [],
    tags: []
  });

  // Initialize form when dataset changes or editing starts
  useEffect(() => {
    if (isEditing && dataset) {
      setEditForm({
        name: dataset.name,
        description: dataset.description,
        status: dataset.status,
        sources: dataset.sources || [],
        fields: dataset.fields || [],
        cacheConfig: dataset.cacheConfig,
        tags: dataset.tags || []
      });
    } else if (isCreating) {
      setEditForm({
        name: '',
        description: '',
        status: 'draft',
        sources: [],
        fields: [],
        tags: []
      });
    }
  }, [isEditing, isCreating, dataset]);

  /**
   * Handle form save
   */
  const handleSave = () => {
    if (!editForm.name) {
      alert('Dataset name is required');
      return;
    }

    onSave(editForm as DatasetConfig);
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✓';
      case 'syncing': return '⟳';
      case 'error': return '⚠️';
      case 'draft': return '📝';
      default: return '○';
    }
  };

  /**
   * Get source type icon
   */
  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'filemaker-odata': return '🗄️';
      case 'filemaker-dataapi': return '🗄️';
      case 'filemaker-script': return '🗄️';
      case 'sql': return '🗃️';
      case 'rest-api': return '🌐';
      case 'csv': return '📄';
      case 'json': return '📋';
      case 'n8n-workflow': return '🔄';
      default: return '📦';
    }
  };

  /**
   * Render editing mode
   */
  if (isEditing) {
    return (
      <div className="dataset-editor-panel">
        <div className="editor-header">
          <h3>{isCreating ? 'Create Dataset' : 'Edit Dataset'}</h3>
        </div>

        <div className="editor-content">
          {/* Basic Info */}
          <div className="form-section">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Dataset name"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="What is this dataset for?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={editForm.status || 'draft'}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="syncing">Syncing</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tags (comma-separated)</label>
              <input
                type="text"
                value={editForm.tags?.join(', ') || ''}
                onChange={(e) => setEditForm({ 
                  ...editForm, 
                  tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                })}
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>

          {/* Data Sources */}
          <div className="form-section">
            <div className="section-header">
              <h4>Data Sources</h4>
              <button className="add-button" disabled title="Coming soon">
                + Add Source
              </button>
            </div>
            <div className="coming-soon-banner">
              <p>📋 Data source configuration coming in Phase 2</p>
              <p className="small">FileMaker, SQL, REST API, and n8n workflow sources</p>
            </div>
            {editForm.sources && editForm.sources.length > 0 && (
              <div className="sources-list">
                {editForm.sources.map(source => (
                  <div key={source.id} className="source-item">
                    <span className="source-icon">{getSourceTypeIcon(source.type)}</span>
                    <span className="source-name">{source.name}</span>
                    <span className="source-type">{source.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Fields */}
          <div className="form-section">
            <div className="section-header">
              <h4>Fields</h4>
              <button className="add-button" disabled title="Coming soon">
                + Add Field
              </button>
            </div>
            <div className="coming-soon-banner">
              <p>🗺️ Field mapping and schema definition coming in Phase 4</p>
            </div>
          </div>

          {/* Cache Configuration */}
          <div className="form-section">
            <div className="section-header">
              <h4>Cache Configuration</h4>
            </div>
            <div className="coming-soon-banner">
              <p>⚡ Cache and TTL configuration coming in Phase 5</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="editor-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="save-button" onClick={handleSave}>
            {isCreating ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render details view
   */
  if (dataset) {
    return (
      <div className="dataset-details-panel">
        <div className="details-header">
          <h3>{dataset.name}</h3>
          <div className="details-actions">
            {dataset.isUserDataset && (
              <button onClick={() => onEdit(dataset)} title="Edit">
                ✏️ Edit
              </button>
            )}
            <button onClick={() => onExport(dataset)} title="Export">
              📤 Export
            </button>
            {dataset.isUserDataset && (
              <button 
                className="delete-button" 
                onClick={() => onDelete(dataset)}
                title="Delete"
              >
                🗑️ Delete
              </button>
            )}
          </div>
        </div>

        <div className="details-content">
          {/* Description */}
          {dataset.description && (
            <div className="details-section">
              <p className="details-description">{dataset.description}</p>
            </div>
          )}

          {/* Status */}
          <div className="details-section">
            <h4>Status</h4>
            <div className={`status-badge status-${dataset.status}`}>
              {getStatusIcon(dataset.status)} {dataset.status}
            </div>
            {!dataset.isUserDataset && (
              <div className="info-badge">
                <span className="badge app-badge">App Dataset (Read-only)</span>
              </div>
            )}
          </div>

          {/* Data Sources */}
          {dataset.sources && dataset.sources.length > 0 && (
            <div className="details-section">
              <h4>Data Sources ({dataset.sources.length})</h4>
              <div className="sources-grid">
                {dataset.sources.map(source => (
                  <div key={source.id} className="source-card">
                    <span className="source-icon">{getSourceTypeIcon(source.type)}</span>
                    <div>
                      <div className="source-name">{source.name}</div>
                      <div className="source-type">{source.type}</div>
                      <div className="source-status">
                        {source.enabled ? '✓ Enabled' : '○ Disabled'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fields */}
          {dataset.fields && dataset.fields.length > 0 && (
            <div className="details-section">
              <h4>Fields ({dataset.fields.length})</h4>
              <table className="fields-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Required</th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.fields.map(field => (
                    <tr key={field.id}>
                      <td>{field.name}</td>
                      <td><code>{field.type}</code></td>
                      <td>{field.required ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tags */}
          {dataset.tags && dataset.tags.length > 0 && (
            <div className="details-section">
              <h4>Tags</h4>
              <div className="tags-list">
                {dataset.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="details-section">
            <h4>Metadata</h4>
            <div className="metadata-grid">
              <div className="metadata-item">
                <span className="metadata-label">Created</span>
                <span className="metadata-value">
                  {new Date(dataset.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Updated</span>
                <span className="metadata-value">
                  {new Date(dataset.updatedAt).toLocaleString()}
                </span>
              </div>
              {dataset.lastSync && (
                <div className="metadata-item">
                  <span className="metadata-label">Last Sync</span>
                  <span className="metadata-value">
                    {new Date(dataset.lastSync).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Sync Error */}
          {dataset.syncError && (
            <div className="details-section error">
              <h4>Sync Error</h4>
              <p className="error-message">{dataset.syncError}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * Render empty state
   */
  return (
    <div className="dataset-empty-panel">
      <div className="empty-state">
        <div className="empty-icon">📊</div>
        <h3>No Dataset Selected</h3>
        <p>Select a dataset from the list to view details</p>
        <p className="empty-hint">or create a new dataset to get started</p>
      </div>
    </div>
  );
}
